import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { House, PencilLine, ChatsCircle, BookOpenText, Buildings, SignOut, ArrowUpRight, FileText, Megaphone } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const NAV = [
  ["/app", "Overview", House, true], ["/app/properties", "Properties", Buildings], ["/app/rewriter", "Listing rewriter", PencilLine],
  ["/app/messages", "Guest messages", ChatsCircle], ["/app/guidebooks", "Guidebooks", BookOpenText], ["/app/templates", "My templates", FileText], ["/app/outreach", "Outreach", Megaphone],
];

export const UsageMeter = () => {
  const { user } = useAuth();
  const pct = Math.min(100, (user.usage / user.limit) * 100);
  const { t } = useT();
  return (
    <div data-testid="usage-meter">
      <div className="flex justify-between text-xs mb-1"><span>{t("AI this month")}</span><span data-testid="usage-count">{user.usage} / {user.limit}</span></div>
      <div className="h-1.5 rounded-full bg-white/20"><div className="h-full rounded-full bg-[#E36414]" style={{ width: `${pct}%`, transition: "width .6s ease" }} /></div>
    </div>
  );
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  return (
    <div className="min-h-screen grid md:grid-cols-[250px_1fr] bg-[#F7F0E4] overflow-x-hidden" data-testid="app-layout">
      <aside className="bg-[#0F4C5C] text-[#F4E9D8] p-5 flex flex-col gap-4 md:gap-6 md:min-h-screen md:sticky md:top-0 md:h-screen min-w-0">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="app-home-link"><Logo light size={26} /></Link>
          <div className="md:hidden flex items-center gap-3 text-xs">
            <span data-testid="mobile-usage-count">{user.usage}/{user.limit} AI · <span className="uppercase font-semibold">{user.plan}</span></span>
            <button onClick={async () => { await logout(); nav("/"); }} className="hover:text-[#E36414]" data-testid="mobile-logout-btn"><SignOut size={20} /></button>
          </div>
        </div>
        <nav className="flex md:flex-col gap-1 md:flex-1 overflow-x-auto -mx-1 px-1">
          {NAV.map(([to, label, I, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`app-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${isActive ? "bg-[#F4E9D8] text-[#0F4C5C] font-semibold" : "hover:bg-white/10"}`}>
              <I size={18} weight="duotone" /> {t(label)}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:block space-y-4">
          <UsageMeter />
          <div className="rounded-xl bg-white/10 p-3 text-xs">
            <p>{t("Plan:")} <span className="font-semibold uppercase" data-testid="app-plan-badge">{user.plan}</span></p>
            {user.plan === "free" && <Link to="/pricing" className="mt-2 inline-flex items-center gap-1 text-[#F9B88A] font-semibold hover:underline" data-testid="app-upgrade-link">{t("Upgrade to Pro")} <ArrowUpRight size={12} /></Link>}
          </div>
          <div className="flex items-center gap-2">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="text-xs min-w-0 flex-1"><p className="font-semibold truncate" data-testid="app-user-name">{user.name}</p><p className="opacity-70 truncate">{user.email}</p></div>
            <button onClick={async () => { await logout(); nav("/"); }} title={t("Sign out")} className="hover:text-[#E36414] transition-colors" data-testid="app-logout-btn"><SignOut size={18} /></button>
          </div>
        </div>
      </aside>
      <main className="p-6 lg:p-10 min-w-0"><Outlet /></main>
    </div>
  );
}
